// The bell's copy for a babysit park (#3769 phase 3): one title and one lead per
// `BabysitEscalationReason`, ported from the worker's `escalation.title.*` /
// `escalation.sentence.*` catalog (`apps/worker/locales/en.json`; apps never import
// apps). TOTAL over the domain's union: a reason added there fails to compile here
// until it has both keys. Literal keys, so the i18n coverage scan sees every one. A
// reason's `:` is `__` in its key (a key segment is `[a-z0-9_]`).

import type { BabysitEscalationReason } from '@developerz/domain';

export const BABYSIT_COPY_KEYS = {
  ci_billing_blocked: {
    title: 'notifications.kind.babysit.title_ci_billing_blocked',
    sentence: 'notifications.kind.babysit.sentence_ci_billing_blocked',
  },
  all_checks_tolerated: {
    title: 'notifications.kind.babysit.title_all_checks_tolerated',
    sentence: 'notifications.kind.babysit.sentence_all_checks_tolerated',
  },
  auto_merge_disabled: {
    title: 'notifications.kind.babysit.title_auto_merge_disabled',
    sentence: 'notifications.kind.babysit.sentence_auto_merge_disabled',
  },
  merge_held: {
    title: 'notifications.kind.babysit.title_merge_held',
    sentence: 'notifications.kind.babysit.sentence_merge_held',
  },
  base_kept_moving: {
    title: 'notifications.kind.babysit.title_base_kept_moving',
    sentence: 'notifications.kind.babysit.sentence_base_kept_moving',
  },
  base_update_refused: {
    title: 'notifications.kind.babysit.title_base_update_refused',
    sentence: 'notifications.kind.babysit.sentence_base_update_refused',
  },
  changes_requested_stale: {
    title: 'notifications.kind.babysit.title_changes_requested_stale',
    sentence: 'notifications.kind.babysit.sentence_changes_requested_stale',
  },
  ci_cancelled: {
    title: 'notifications.kind.babysit.title_ci_cancelled',
    sentence: 'notifications.kind.babysit.sentence_ci_cancelled',
  },
  ci_fix_declined: {
    title: 'notifications.kind.babysit.title_ci_fix_declined',
    sentence: 'notifications.kind.babysit.sentence_ci_fix_declined',
  },
  ci_not_converging: {
    title: 'notifications.kind.babysit.title_ci_not_converging',
    sentence: 'notifications.kind.babysit.sentence_ci_not_converging',
  },
  ci_self_fix_disabled: {
    title: 'notifications.kind.babysit.title_ci_self_fix_disabled',
    sentence: 'notifications.kind.babysit.sentence_ci_self_fix_disabled',
  },
  forge_action_unsupported: {
    title: 'notifications.kind.babysit.title_forge_action_unsupported',
    sentence: 'notifications.kind.babysit.sentence_forge_action_unsupported',
  },
  ci_unfixable: {
    title: 'notifications.kind.babysit.title_ci_unfixable',
    sentence: 'notifications.kind.babysit.sentence_ci_unfixable',
  },
  ci_wait_timeout: {
    title: 'notifications.kind.babysit.title_ci_wait_timeout',
    sentence: 'notifications.kind.babysit.sentence_ci_wait_timeout',
  },
  dead_owner_no_automerge: {
    title: 'notifications.kind.babysit.title_dead_owner_no_automerge',
    sentence: 'notifications.kind.babysit.sentence_dead_owner_no_automerge',
  },
  finding_format_drift: {
    title: 'notifications.kind.babysit.title_finding_format_drift',
    sentence: 'notifications.kind.babysit.sentence_finding_format_drift',
  },
  findings_not_converging: {
    title: 'notifications.kind.babysit.title_findings_not_converging',
    sentence: 'notifications.kind.babysit.sentence_findings_not_converging',
  },
  findings_unresolved: {
    title: 'notifications.kind.babysit.title_findings_unresolved',
    sentence: 'notifications.kind.babysit.sentence_findings_unresolved',
  },
  followup_held: {
    title: 'notifications.kind.babysit.title_followup_held',
    sentence: 'notifications.kind.babysit.sentence_followup_held',
  },
  followup_unplaceable: {
    title: 'notifications.kind.babysit.title_followup_unplaceable',
    sentence: 'notifications.kind.babysit.sentence_followup_unplaceable',
  },
  followup_unrepeatable: {
    title: 'notifications.kind.babysit.title_followup_unrepeatable',
    sentence: 'notifications.kind.babysit.sentence_followup_unrepeatable',
  },
  github_blocks: {
    title: 'notifications.kind.babysit.title_github_blocks',
    sentence: 'notifications.kind.babysit.sentence_github_blocks',
  },
  github_refused_stuck: {
    title: 'notifications.kind.babysit.title_github_refused_stuck',
    sentence: 'notifications.kind.babysit.sentence_github_refused_stuck',
  },
  human_merge_lane: {
    title: 'notifications.kind.babysit.title_human_merge_lane',
    sentence: 'notifications.kind.babysit.sentence_human_merge_lane',
  },
  max_sweeps_exceeded: {
    title: 'notifications.kind.babysit.title_max_sweeps_exceeded',
    sentence: 'notifications.kind.babysit.sentence_max_sweeps_exceeded',
  },
  merge_conflict: {
    title: 'notifications.kind.babysit.title_merge_conflict',
    sentence: 'notifications.kind.babysit.sentence_merge_conflict',
  },
  merge_decision: {
    title: 'notifications.kind.babysit.title_merge_decision',
    sentence: 'notifications.kind.babysit.sentence_merge_decision',
  },
  'merge_refused:auto_merge_off': {
    title: 'notifications.kind.babysit.title_merge_refused__auto_merge_off',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__auto_merge_off',
  },
  'merge_refused:merge_held': {
    title: 'notifications.kind.babysit.title_merge_refused__merge_held',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__merge_held',
  },
  'merge_refused:author_out_of_scope': {
    title: 'notifications.kind.babysit.title_merge_refused__author_out_of_scope',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__author_out_of_scope',
  },
  'merge_refused:finding_format_drift': {
    title: 'notifications.kind.babysit.title_merge_refused__finding_format_drift',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__finding_format_drift',
  },
  'merge_refused:gate_path_edit_unproven': {
    title: 'notifications.kind.babysit.title_merge_refused__gate_path_edit_unproven',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__gate_path_edit_unproven',
  },
  'merge_refused:gate_path_pin_unverified': {
    title: 'notifications.kind.babysit.title_merge_refused__gate_path_pin_unverified',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__gate_path_pin_unverified',
  },
  'merge_refused:gate_path_widens': {
    title: 'notifications.kind.babysit.title_merge_refused__gate_path_widens',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__gate_path_widens',
  },
  'merge_refused:brief_over_cap': {
    title: 'notifications.kind.babysit.title_merge_refused__brief_over_cap',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__brief_over_cap',
  },
  'merge_refused:github_blocks': {
    title: 'notifications.kind.babysit.title_merge_refused__github_blocks',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__github_blocks',
  },
  'merge_refused:merge_decision': {
    title: 'notifications.kind.babysit.title_merge_refused__merge_decision',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__merge_decision',
  },
  'merge_refused:external_change_refused': {
    title: 'notifications.kind.babysit.title_merge_refused__external_change_refused',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__external_change_refused',
  },
  'merge_refused:strict_review_refused': {
    title: 'notifications.kind.babysit.title_merge_refused__strict_review_refused',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__strict_review_refused',
  },
  'merge_refused:external_unreadable_files': {
    title: 'notifications.kind.babysit.title_merge_refused__external_unreadable_files',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__external_unreadable_files',
  },
  'merge_refused:outsider_touches_gate_paths': {
    title: 'notifications.kind.babysit.title_merge_refused__outsider_touches_gate_paths',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__outsider_touches_gate_paths',
  },
  'merge_refused:review_unserved': {
    title: 'notifications.kind.babysit.title_merge_refused__review_unserved',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__review_unserved',
  },
  'merge_refused:screen_held_pr_text': {
    title: 'notifications.kind.babysit.title_merge_refused__screen_held_pr_text',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__screen_held_pr_text',
  },
  'merge_refused:stale_base': {
    title: 'notifications.kind.babysit.title_merge_refused__stale_base',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__stale_base',
  },
  'merge_refused:unresolved_findings': {
    title: 'notifications.kind.babysit.title_merge_refused__unresolved_findings',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__unresolved_findings',
  },
  'merge_refused:unresolved_own_threads': {
    title: 'notifications.kind.babysit.title_merge_refused__unresolved_own_threads',
    sentence: 'notifications.kind.babysit.sentence_merge_refused__unresolved_own_threads',
  },
  own_review_hold_stale: {
    title: 'notifications.kind.babysit.title_own_review_hold_stale',
    sentence: 'notifications.kind.babysit.sentence_own_review_hold_stale',
  },
  pr_intent_reverted: {
    title: 'notifications.kind.babysit.title_pr_intent_reverted',
    sentence: 'notifications.kind.babysit.sentence_pr_intent_reverted',
  },
  pre_push_refused: {
    title: 'notifications.kind.babysit.title_pre_push_refused',
    sentence: 'notifications.kind.babysit.sentence_pre_push_refused',
  },
  review_absent: {
    title: 'notifications.kind.babysit.title_review_absent',
    sentence: 'notifications.kind.babysit.sentence_review_absent',
  },
  review_approval_stale: {
    title: 'notifications.kind.babysit.title_review_approval_stale',
    sentence: 'notifications.kind.babysit.sentence_review_approval_stale',
  },
  review_in_flight: {
    title: 'notifications.kind.babysit.title_review_in_flight',
    sentence: 'notifications.kind.babysit.sentence_review_in_flight',
  },
  screen_unavailable_stale: {
    title: 'notifications.kind.babysit.title_screen_unavailable_stale',
    sentence: 'notifications.kind.babysit.sentence_screen_unavailable_stale',
  },
} as const satisfies Readonly<
  Record<BabysitEscalationReason, { readonly title: string; readonly sentence: string }>
>;

/** `ci_fix_declined` when GitHub refused the platform's re-run first (`rerun_refused`). */
export const BABYSIT_RERUN_REFUSED_KEY =
  'notifications.kind.babysit.sentence_rerun_refused_ci_fix_declined';
